import chroma from "chroma-js";

/**
 * Generates a deterministic color from a string using FNV-1a hash
 * with golden angle hue distribution for maximum visual separation.
 */
export function stringToColor(str: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  hash = hash >>> 0;

  const hue = (hash * 137.508) % 360;
  const saturation = 55 + (((hash >>> 10) ^ (hash >>> 20)) % 25);
  const lightness = 45 + (((hash >>> 15) ^ (hash >>> 25)) % 20);

  return chroma.hsl(hue, saturation / 100, lightness / 100).hex();
}

/** Returns a tinted background + matching foreground color pair for a string. */
export function stringToColorPair(str: string): {
  background: string;
  foreground: string;
} {
  const base = stringToColor(str);
  const [h, s] = chroma(base).hsl();
  return {
    background: chroma.hsl(h, Math.min(s * 0.8, 0.6), 0.92).hex(),
    foreground: chroma.hsl(h, Math.min(s * 0.9, 0.7), 0.35).hex(),
  };
}

/** Dark mode variant — muted, lower-saturation badge color. */
export function stringToColorDark(str: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  hash = hash >>> 0;

  const hue = (hash * 137.508) % 360;
  const saturation = 35 + (((hash >>> 10) ^ (hash >>> 20)) % 15);
  const lightness = 30 + (((hash >>> 15) ^ (hash >>> 25)) % 10);

  return chroma.hsl(hue, saturation / 100, lightness / 100).hex();
}

/** Dark mode variant — darker tinted background, lighter foreground. */
export function stringToColorPairDark(str: string): {
  background: string;
  foreground: string;
} {
  const base = stringToColor(str);
  const [h, s] = chroma(base).hsl();
  return {
    background: chroma.hsl(h, Math.min(s * 0.5, 0.4), 0.18).hex(),
    foreground: chroma.hsl(h, Math.min(s * 0.7, 0.6), 0.7).hex(),
  };
}
